// src/components/ContinuityReportButton.jsx
// Bouton « Rapport de continuité (PDF) » au niveau collection.
// Couvre tous les ensembles qualifiés (nature renseignée) du même propriétaire, plus la collection courante.
// Œuvres lues dans artworks_secure (masques de droits respectés) ; invoice vient de la table artworks.
import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { generateContinuityReport } from "../utils/continuityReportPdf";
import { useTranslation } from "react-i18next";

const EVENT_TYPES = ["exhibition", "loan", "location_change", "transport", "collection_entry", "sale_mandate", "sale", "donation", "deaccession"];

const COPY = {
  fr: { busy: "Préparation…", label: "Rapport de continuité (PDF)", title: "Rapport préparatoire : ensembles, statuts patrimoniaux, points à clarifier, accès, démarches", fail: "Impossible de générer le rapport de continuité.", notFound: "Collection introuvable" },
  en: { busy: "Preparing…", label: "Continuity report (PDF)", title: "Preparatory report: collections, heritage statuses, points to clarify, access, next steps", fail: "Unable to generate the continuity report.", notFound: "Collection not found" },
  de: { busy: "Wird vorbereitet…", label: "Kontinuitätsbericht (PDF)", title: "Vorbereitender Bericht: Bestände, Besitzstatus, zu klärende Punkte, Zugriffe, nächste Schritte", fail: "Der Kontinuitätsbericht konnte nicht erstellt werden.", notFound: "Sammlung nicht gefunden" },
};

export default function ContinuityReportButton({ collectionId, ownerName = "", className = "" }) {
  const [busy, setBusy] = useState(false);
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const cp = COPY[lang] || COPY.fr;

  const run = async () => {
    if (!collectionId || busy) return;
    setBusy(true);
    try {
      const { data: current, error: cErr } = await supabase
        .from("collections")
        .select("id, title, client_id, ensemble_nature, transmission_context")
        .eq("id", collectionId)
        .maybeSingle();
      if (cErr) throw cErr;
      if (!current) throw new Error(cp.notFound);

      // Ensembles qualifiés du même propriétaire + la collection courante.
      const { data: qualified } = await supabase
        .from("collections")
        .select("id, title, client_id, ensemble_nature, transmission_context")
        .eq("client_id", current.client_id)
        .not("ensemble_nature", "is", null);
      const byId = new Map([[current.id, current]]);
      (qualified || []).forEach((c) => byId.set(c.id, c));
      const cols = [...byId.values()];
      const colIds = cols.map((c) => c.id);

      const { data: arts, error: aErr } = await supabase
        .from("artworks_secure")
        .select("id, collection_id, title, artist, heritage_status, ownership_verification, provenance, documents")
        .in("collection_id", colIds)
        .limit(5000);
      if (aErr) throw aErr;

      const artIds = (arts || []).map((a) => a.id);
      const invoiceById = {};
      if (artIds.length) {
        const { data: inv } = await supabase.from("artworks").select("id, invoice").in("id", artIds.slice(0, 1000));
        (inv || []).forEach((r) => { invoiceById[r.id] = r.invoice === true; });
      }
      const artworks = (arts || []).map((a) => ({ ...a, invoice: invoiceById[a.id] === true }));

      const collections = cols.map((c) => ({
        id: c.id,
        title: c.title,
        nature: c.ensemble_nature,
        context: c.transmission_context,
        artworks: artworks.filter((a) => a.collection_id === c.id),
      }));

      // Expositions et mouvements (extrait : 25 derniers événements)
      let events = [];
      if (artIds.length) {
        const { data: ev } = await supabase
          .from("artwork_lifecycle_events")
          .select("artwork_id, event_type, title, event_date_start")
          .in("artwork_id", artIds.slice(0, 1000))
          .in("event_type", EVENT_TYPES)
          .is("deleted_at", null)
          .order("event_date_start", { ascending: false })
          .limit(25);
        const titleById = Object.fromEntries(artworks.map((a) => [a.id, a.title]));
        events = (ev || []).map((e) => ({ ...e, artwork_title: titleById[e.artwork_id] }));
      }

      // Accès : droits par collection + liens de partage actifs
      const grants = [];
      const { data: perms } = await supabase
        .from("collection_permissions")
        .select("collection_id, grantee_id, can_view, can_edit")
        .in("collection_id", colIds);
      const granteeIds = [...new Set((perms || []).map((p) => p.grantee_id).filter(Boolean))];
      let names = {};
      if (granteeIds.length) {
        const { data: profs } = await supabase.from("profiles").select("id, first_name, last_name").in("id", granteeIds);
        names = Object.fromEntries((profs || []).map((p) => [p.id, [p.first_name, p.last_name].filter(Boolean).join(" ")]));
      }
      const titleByCol = Object.fromEntries(cols.map((c) => [c.id, c.title]));
      (perms || []).filter((p) => p.can_view).forEach((p) => {
        grants.push({
          collection_title: titleByCol[p.collection_id],
          grantee_name: names[p.grantee_id] || "",
          level: p.can_edit ? "read_edit" : "read",
        });
      });

      const { data: sh } = await supabase
        .from("dossier_shares")
        .select("recipient_label, recipient_kind, expires_at, revoked_at")
        .eq("client_id", current.client_id)
        .is("revoked_at", null)
        .gt("expires_at", new Date().toISOString());
      const shares = (sh || []).map((s) => ({ label: s.recipient_label || s.recipient_kind, kind: s.recipient_kind, expires_at: s.expires_at }));

      generateContinuityReport({ ownerName: ownerName || current.title, collections, events, grants, shares, lang });
    } catch (e) {
      console.error("[ContinuityReportButton]", e);
      window.alert(cp.fail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || !collectionId}
      title={cp.title}
      className={className || "inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"}
    >
      {busy ? cp.busy : cp.label}
    </button>
  );
}
